/* eslint-disable react/prop-types, import/no-extraneous-dependencies */
import React, { useEffect, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import nProgress from "nprogress";
import { Modal } from "react-bootstrap";
import {
  getBroadcastMessage,
  getRelatedBroadcastMessage,
  getReplies,
  getRepliesByBroadcastMessage,
  getRepliesByMessageContext,
} from "./ReportsAPI";

const PER_PAGE = 10;

export function formatReplyDateTime(value) {
  if (!value) return "-";

  const date = new Date(`${value.split(" ").join("T")}Z`);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
}

function RepliesModal({ showReplies, setShowReplies }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotError, setSnapshotError] = useState("");

  const handleClose = () => {
    setShowReplies(null);
    setPage(1);
  };

  const tableChangeHandler = (type, { page: newPage }) => {
    setPage(newPage);
  };

  const columns = [
    { dataField: "id", text: "ID" },
    { dataField: "responder", text: "Responder number" },
    {
      dataField: "dateTime",
      text: "Date/Time",
      formatter: formatReplyDateTime,
    },
    { dataField: "message", text: "Reply" },
  ];

  useEffect(() => {
    if (showReplies === null) return undefined;

    const controller = new AbortController();
    const uuid = showReplies?.broadcastMessageUuid;
    const context = showReplies?.context;
    nProgress.start();
    setData(null);
    setSnapshot(null);
    setSnapshotError("");

    let repliesRequest;
    if (context) {
      repliesRequest = getRepliesByMessageContext(
        { ...context, offset: (page - 1) * PER_PAGE, limit: PER_PAGE },
        controller
      );
    } else if (uuid) {
      repliesRequest = getRepliesByBroadcastMessage(
        { uuid, offset: (page - 1) * PER_PAGE, limit: PER_PAGE },
        controller
      );
    } else {
      repliesRequest = getReplies(
        {
          transactionId: showReplies?.id,
          offset: (page - 1) * PER_PAGE,
          limit: PER_PAGE,
        },
        controller
      );
    }

    let snapshotRequest = Promise.resolve(null);
    if (context) snapshotRequest = getRelatedBroadcastMessage(context, controller);
    else if (uuid) snapshotRequest = getBroadcastMessage(uuid, controller);
    snapshotRequest = snapshotRequest.catch((error) => {
      if (error.name !== "AbortError") setSnapshotError(error.message);
      return null;
    });

    Promise.allSettled([repliesRequest, snapshotRequest]).then((results) => {
      if (!controller.signal.aborted) {
        if (results[0].status === "fulfilled") setData(results[0].value);
        else {
          setData({
            rows: [],
            total: 0,
            error: results[0].reason?.message || "Failed to fetch replies",
          });
        }
        if (results[1].status === "fulfilled") setSnapshot(results[1].value);
      }
      nProgress.done();
    });

    return () => controller.abort();
  }, [showReplies, page]);

  return (
    <Modal
      show={showReplies !== null}
      onHide={handleClose}
      size={(snapshot?.channel || showReplies?.channel) === "email" ? "lg" : undefined}
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="icon fa fa-comments" /> Message and replies
          <strong className="badge badge-default pull-right margin-right-20">
            {data?.total ?? 0} replies
          </strong>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h4>Message</h4>
        {snapshot?.email_subject && <h5>{snapshot.email_subject}</h5>}
        {snapshot?.message || showReplies?.message ? (
          <div
            dangerouslySetInnerHTML={{
              __html: snapshot?.message ?? showReplies?.message,
            }}
          />
        ) : (
          <div className="text-secondary">Message not available</div>
        )}
        {snapshot && (
          <dl className="row mt-3">
            <dt className="col-sm-3">Channel</dt>
            <dd className="col-sm-9">{snapshot.channel}</dd>
            {snapshot.email_from && (
              <>
                <dt className="col-sm-3">From</dt>
                <dd className="col-sm-9">{snapshot.email_from}</dd>
              </>
            )}
            {snapshot.channel === "voice" && (
              <>
                <dt className="col-sm-3">Retries</dt>
                <dd className="col-sm-9">{snapshot.voice_retries}</dd>
                <dt className="col-sm-3">Retry delay</dt>
                <dd className="col-sm-9">
                  {snapshot.voice_delay_minutes} minutes
                </dd>
                <dt className="col-sm-3">Redirect</dt>
                <dd className="col-sm-9">{snapshot.voice_redirect || "-"}</dd>
              </>
            )}
          </dl>
        )}
        {snapshotError && <div className="text-center text-danger">{snapshotError}</div>}
        {data?.total > 0 && (
          <>
            <hr />
            <h4>Replies</h4>
            <BootstrapTable
              remote
              keyField="id"
              data={data?.rows ?? []}
              columns={columns}
              bordered={false}
              hover
              pagination={paginationFactory({
                page,
                sizePerPage: PER_PAGE,
                totalSize: data?.total ?? 0,
                hidePageListOnlyOnePage: true,
                hideSizePerPage: true,
              })}
              onTableChange={tableChangeHandler}
            />
          </>
        )}
        <div className="text-center text-danger">{data?.error ?? ""}</div>
      </Modal.Body>
    </Modal>
  );
}

export default RepliesModal;
